import React, { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { theme, Empty, Select } from "antd";
import ComponentHeader from "app/shared/ui/ComponentHeader";
import ErrorSummary from "app/pages/DiscountEditor/components/ErrorSummary";
import { getSectionError } from "app/pages/DiscountEditor/modules/Editor";
import { SaveDiscountErrorCategory } from "app/shared/model/SaveDiscountResultDto";
import "./DivisionSelectionEditor.css";

import { getDivisions } from "app/pages/Division/selectors";
import { loadDivisions, DIVISIONS_LOADED } from "app/pages/Division/actions";
import * as divApi from "app/shared/api/getProductDivisions";

import { getSelectedDivisionIds } from "../modules/Divisions/selectors";
import { changeDivisionSelectionCommand } from "../commands";

const { useToken } = theme;
const Option = Select.Option;

type InternalProps = StateProps;

interface StateProps {
  errorMessage?: string;
  divisions: { id: number; name: string }[];
  selectedDivisionIds: number[];
}

const DivisionSelectionEditor: React.FC<InternalProps> = ({
  errorMessage,
  divisions,
  selectedDivisionIds,
}) => {
  const { token } = useToken();
  const dispatch = useDispatch();
  const hasLoadedRef = useRef(false);
  const [localDivisions, setLocalDivisions] = useState<{ id: number; name: string }[] | null>(null);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    if (divisions && divisions.length > 0) {
      hasLoadedRef.current = true;
      return;
    }

    (dispatch as any)(loadDivisions());
    
    const fetchDirect = async () => {
      try {
        const rows = await divApi.getAllProductDivisions();
        if (rows && rows.length > 0) {
          setLocalDivisions(rows);
          (dispatch as any)({ type: DIVISIONS_LOADED, divisions: rows });
          hasLoadedRef.current = true;
        }
      } catch (e) {
      }
    };
    
    fetchDirect();
  }, []);

  const list = useMemo(
    () => {
      if (divisions && divisions.length > 0) {
        return divisions;
      }
      return localDivisions || [];
    },
    [divisions, localDivisions]
  );

  const handleChange = useCallback(
    (value: any) => {
      const numericId = typeof value === 'number' ? value : Number(value);

      // Only dispatch if it's a valid number
      if (!isNaN(numericId)) {
        (dispatch as any)(changeDivisionSelectionCommand([numericId]));
      } else {
        // If no valid selection, reset
        (dispatch as any)(changeDivisionSelectionCommand([]));
      }
    },
    [dispatch]
  );

  return (
    <div className="division-selection-editor">
      <ComponentHeader text="Division" />
      <div className="division-selection-editor__content">
        <ErrorSummary errorMessage={errorMessage} />
      {list.length === 0 ? (
        <Empty description="No divisions found" />
      ) : (
        <Select
          value={selectedDivisionIds.length > 0 ? selectedDivisionIds[0] : undefined}
          onChange={handleChange}
          placeholder="Select division"
          className="division-selection-editor__select"
        >
          {list.map((d) => (
            <Option key={d.id} value={d.id}>
              {d.name}
            </Option>
          ))}
        </Select>
      )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  errorMessage: getSectionError(state, SaveDiscountErrorCategory.Divisions),
  divisions: getDivisions(state),
  selectedDivisionIds: getSelectedDivisionIds(state),
});

export default connect(mapStateToProps)(DivisionSelectionEditor);